/* eslint-disable import/no-extraneous-dependencies */
import { ApolloClient, ApolloLink, from, InMemoryCache, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { createHttpLink } from '@apollo/client/link/http';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient, Client as GraphQLWsClient } from 'graphql-ws';
import { v4 } from 'uuid';
import { OperationDefinitionNode } from 'graphql';
import { createUploadLink } from 'apollo-upload-client';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';

import { StorageUtils, Logger } from '../utils';
import environment from '../environment';

// Load Apollo Client dev messages in development
if (process.env.NODE_ENV === 'development') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

// Use ws:// for localhost development, wss:// for production
const protocol = environment.SUB_URL.includes('localhost') ? 'ws' : 'wss';
const uri = `${protocol}://${environment.SUB_URL}/graphql`;
const apiUri = `${environment.API_URL}/graphql`;

let id: string;
const getNewId = () => {
  id = v4();
  return id;
};

const shouldUseWebSocket = typeof window !== 'undefined';

// Function to create Apollo client with auth token
export const createApolloClient = (getAuthToken: () => string | undefined) => {
  const createWsClient = (): GraphQLWsClient | null => {
    if (!shouldUseWebSocket) {
      return null;
    }

    return createClient({
      url: uri,
      lazy: true,
      keepAlive: 30000,
      retryAttempts: Infinity,
      shouldRetry: () => {
        return true;
      },
      connectionParams: () => {
        const token = getAuthToken();

        return {
          authToken: token ? `Bearer ${token}` : undefined,
          clientId: !id ? getNewId() : id,
          selectedCustomer: StorageUtils.getCustomerId(),
        };
      },
    });
  };

  const subscriptionClient = createWsClient();
  const wsLink = subscriptionClient ? new GraphQLWsLink(subscriptionClient) : null;

  const authLink = setContext((context, { headers }) => {
    const newHeaders = { ...headers };
    const token = getAuthToken();
    const customerId = StorageUtils.getCustomerId();

    if (token) {
      newHeaders.Authorization = `Bearer ${token}`;
    }

    if (customerId) {
      newHeaders.selectedCustomer = customerId;
    }

    return { headers: newHeaders };
  });

  const httpLink = from([authLink, createHttpLink({ uri: apiUri, preserveHeaderCase: true })]);

  const httpOrWsLink = wsLink
    ? split(
        ({ query }) => {
          const { kind, operation } = getMainDefinition(query) as OperationDefinitionNode;
          return kind === 'OperationDefinition' && operation === 'subscription';
        },
        wsLink,
        httpLink,
      )
    : httpLink;

  const uploadLink = ApolloLink.from([
    authLink,
    createUploadLink({ uri: apiUri, includeExtensions: true }),
  ]);

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) => {
        return Logger.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
      });
    }

    if (networkError) {
      const errorMessage = networkError.message || String(networkError);
      const operationName = operation?.operationName || 'unknown';
      const operationType = operation?.query?.definitions?.[0]?.kind || 'unknown';

      Logger.error(`[Network error]: ${errorMessage}`);
      Logger.error(`[Network error details]: Operation: ${operationName}, Type: ${operationType}`);
      Logger.error(`[Network error details]: API URL: ${apiUri}`);

      // Provide helpful error message for common issues
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        console.error(
          `\n⚠️  Network Error: Cannot connect to GraphQL API at ${apiUri}\n` +
            `   Possible causes:\n` +
            `   1. Backend API server is not running\n` +
            `   2. Backend API is running on a different port\n` +
            `   3. CORS configuration issue\n` +
            `   4. Network connectivity problem\n\n` +
            `   Please check:\n` +
            `   - Is the backend API running?\n` +
            `   - Is the API_URL correct? (currently: ${apiUri})\n` +
            `   - Check browser console for CORS errors\n`,
        );
      }
    }
  });

  const link = ApolloLink.split(
    (operation) => {
      return operation.getContext().hasUpload;
    },
    uploadLink,
    httpOrWsLink,
  );

  return new ApolloClient({
    link: from([errorLink, link]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
  });
};

// Legacy export for backward compatibility - will be removed
export const subscriptionClient = null;
export const client = null;
